import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Tabs, Tab, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel, Grid, Card, CardContent, Alert, Pagination, Collapse,
  TableSortLabel, Badge, Divider, Button, Dialog, DialogContent,
  TablePagination,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CategoryIcon from '@mui/icons-material/Category';
import CollectionsIcon from '@mui/icons-material/Collections';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import InventoryIcon from '@mui/icons-material/Inventory';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import RefreshIcon from '@mui/icons-material/Refresh';
import InterfaceHeader from '../components/InterfaceHeader';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { useSnackbar } from 'notistack';

const ASSET_COLOR = '#0EA5E9';
const ASSET_HOVER = '#0284C7';
const ASSET_GRADIENT = 'linear-gradient(135deg, #0EA5E9 0%, #6366f1 100%)';

const VIEW_TYPE_COLORS = {
  'ProductShot': '#3b82f6', 'PRODUCT': '#3b82f6', 'Room': '#10b981', 'Detail': '#f59e0b',
  'Front': '#8b5cf6', 'Back': '#ec4899', 'Side': '#6366f1',
  'Top': '#14b8a6', 'Bottom': '#a855f7', 'Swatch': '#6b7280',
  'Dimensions': '#78716c', '3DRoom': '#ef4444', 'RoomAI': '#f43f5e',
  'Composite': '#0891b2', 'Set': '#059669', 'Corner': '#d97706',
};

const ITEMS_PER_PAGE = 50;

const AssetExplorer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { enqueueSnackbar } = useSnackbar();
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const { addActivityProcess, completeActivityProcess } = useActivityLog();

  // State
  const [activeTab, setActiveTab] = useState(0); // 0=Gallery, 1=Table, 2=Graph
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [viewTypeFilter, setViewTypeFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Table sorting
  const [orderBy, setOrderBy] = useState('fileName');
  const [order, setOrder] = useState('asc');

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  // Graph state — hoisted here so theme changes don't reset it
  const [gNodes, setGNodes] = useState([]);
  const [gLinks, setGLinks] = useState([]);
  const [gExpanded, setGExpanded] = useState(new Set());
  const [gSelected, setGSelected] = useState(null);
  const [gHovered, setGHovered] = useState(null);
  const [gLoading, setGLoading] = useState(false);
  const [gBreadcrumbs, setGBreadcrumbs] = useState([]);
  const [gDetail, setGDetail] = useState(null);
  const [gPan, setGPan] = useState({ x: 0, y: 0 });
  const [gZoom, setGZoom] = useState(1);
  const [gSearch, setGSearch] = useState('');
  const [gSuggestions, setGSuggestions] = useState([]);
  const [gShowSuggestions, setGShowSuggestions] = useState(false);
  const [gHighlighted, setGHighlighted] = useState(new Set());
  const [gHistory, setGHistory] = useState([]);
  const [gTypeFilter, setGTypeFilter] = useState('');
  const [gImagePanel, setGImagePanel] = useState(false); // image preview panel
  const [gAssetImages, setGAssetImages] = useState([]); // collected asset images
  const [gInitialized, setGInitialized] = useState(false);

  const searchTimeout = useRef(null);
  const abortController = useRef(null);
  const gDragRef = useRef(null);
  const gSuggestTimeout = useRef(null);
  const gContainerRef = useRef(null);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await apiCall(API_ENDPOINTS.ASSET_EXPLORER_STATS);
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const searchAssets = useCallback(async (query, cat, viewType, pageNum, customPageSize) => {
    if (abortController.current) abortController.current.abort();
    abortController.current = new AbortController();

    const ps = customPageSize || rowsPerPage;
    if (customPageSize) setRowsPerPage(customPageSize);

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (cat) params.append('category', cat);
      if (viewType) params.append('viewType', viewType);
      params.append('page', pageNum);
      params.append('pageSize', ps);
      params.append('sortBy', orderBy);
      params.append('sortDir', order);

      const data = await apiCall(
        `${API_ENDPOINTS.ASSET_EXPLORER_SEARCH}?${params.toString()}`,
        { signal: abortController.current.signal }
      );
      const allResults = data.results || [];
      setAssets(allResults.slice(0, ps));
      setTotalResults(data.total || 0);
      if (query) addActivityProcess(`search-${Date.now()}`, `Searched "${query}" — ${data.total || 0} results`, 'asset-explorer');
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        setAssets([]);
      }
    } finally {
      setLoading(false);
    }
  }, [orderBy, order]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      searchAssets(searchQuery, categoryFilter, viewTypeFilter, page);
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, categoryFilter, viewTypeFilter, page, searchAssets]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getViewTypeColor = (vt) => VIEW_TYPE_COLORS[vt] || '#6b7280';

  // =========================================================================
  // Stats Cards
  // =========================================================================
  const StatsBar = () => {
    if (!stats) return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[0,1,2,3].map(i => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{ bgcolor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 3, overflow: 'hidden' }}>
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: isDark ? '#0f172a' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={16} sx={{ color: ASSET_COLOR }} />
                  </Box>
                  <Box><Box sx={{ width: 60, height: 20, borderRadius: 1, bgcolor: isDark ? '#0f172a' : '#f1f5f9', mb: 0.5 }} /><Box sx={{ width: 80, height: 12, borderRadius: 0.5, bgcolor: isDark ? '#0f172a' : '#f1f5f9' }} /></Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
    const gradients = ['linear-gradient(135deg, #0EA5E9, #3b82f6)', 'linear-gradient(135deg, #8b5cf6, #6366f1)', 'linear-gradient(135deg, #10b981, #059669)', 'linear-gradient(135deg, #f59e0b, #d97706)'];
    const cards = [
      { icon: <PhotoLibraryIcon />, value: stats.totalAssets?.toLocaleString() || '0', label: 'Total Assets' },
      { icon: <CategoryIcon />, value: stats.totalCategories || '0', label: 'Categories' },
      { icon: <CollectionsIcon />, value: stats.totalCollections || '0', label: 'Collections' },
      { icon: <InventoryIcon />, value: stats.totalSkuGroups || '0', label: 'SKU Groups' },
    ];
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((c, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{
              bgcolor: isDark ? '#1e293b' : '#fff',
              border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.3)' : '0 8px 24px rgba(0,0,0,0.08)' },
            }}>
              <Box sx={{ height: 3, background: gradients[i] }} />
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: gradients[i], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: `0 4px 12px ${ASSET_COLOR}30` }}>
                    {c.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={800} sx={{ color: isDark ? '#f8fafc' : '#0f172a', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                      {c.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 11, fontWeight: 500, letterSpacing: 0.3 }}>
                      {c.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // =========================================================================
  // View Type Distribution
  // =========================================================================
  const ViewTypeChart = () => {
    if (!stats?.viewTypes?.length) return null;
    return (
      <Box sx={{ mb: 2.5, p: 2, bgcolor: isDark ? '#1e293b' : '#fff', borderRadius: 3, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` }}>
        <Typography variant="subtitle2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, fontWeight: 600 }}>
          Quick Filter by View Type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {stats.viewTypes.slice(0, 15).map((vt) => {
            const active = viewTypeFilter === vt.name;
            const color = getViewTypeColor(vt.name);
            return (
              <Chip key={vt.name}
                label={`${vt.name} ${vt.count.toLocaleString()}`}
                size="small"
                onClick={() => { setViewTypeFilter(active ? '' : vt.name); setPage(1); }}
                sx={{
                  bgcolor: active ? color : (isDark ? `${color}15` : `${color}10`),
                  color: active ? '#fff' : color,
                  border: `1px solid ${active ? color : `${color}30`}`,
                  fontWeight: 600, fontSize: 11, height: 28, cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: active ? `0 2px 8px ${color}40` : 'none',
                  '&:hover': { bgcolor: active ? color : `${color}20`, transform: 'translateY(-1px)' },
                }}
              />
            );
          })}
          {viewTypeFilter && (
            <Chip label="Clear" size="small" onClick={() => { setViewTypeFilter(''); setPage(1); }}
              sx={{ height: 28, cursor: 'pointer', color: isDark ? '#94a3b8' : '#64748b', border: `1px dashed ${isDark ? '#475569' : '#cbd5e1'}`, fontSize: 11 }} />
          )}
        </Box>
      </Box>
    );
  };

  // =========================================================================
  // Gallery View
  // =========================================================================
  const [galLightbox, setGalLightbox] = useState(-1); // -1 = closed
  const [galZoom, setGalZoom] = useState(false);

  // Gallery lightbox keyboard nav
  useEffect(() => {
    if (galLightbox < 0) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft' && galLightbox > 0) { setGalLightbox(i => i - 1); setGalZoom(false); }
      else if (e.key === 'ArrowRight' && galLightbox < assets.length - 1) { setGalLightbox(i => i + 1); setGalZoom(false); }
      else if (e.key === 'Escape') { setGalLightbox(-1); setGalZoom(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [galLightbox, assets.length]);

  const downloadAsset = async (url, name) => {
    const processId = `download-${Date.now()}`;
    addActivityProcess(processId, `Downloading ${name}`, 'asset-explorer');
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = blobUrl; a.download = name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      enqueueSnackbar(`Downloaded ${name}`, { variant: 'success', autoHideDuration: 3000 });
      completeActivityProcess(processId, 'success');
    } catch {
      window.open(url, '_blank');
      enqueueSnackbar(`Opened ${name} in new tab`, { variant: 'info', autoHideDuration: 3000 });
      completeActivityProcess(processId, 'success', 'Opened in new tab');
    }
  };

  const GalleryView = () => (
    <Box>
      {/* Top pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <TablePagination
          component="div"
          count={totalResults}
          page={page - 1}
          onPageChange={(e, newPage) => setPage(newPage + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { const v = parseInt(e.target.value, 10); setRowsPerPage(v); setPage(1); searchAssets(searchQuery, categoryFilter, viewTypeFilter, 1, v); }}
          rowsPerPageOptions={[50, 100, 200]}
          sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
        />
      </Box>

      {/* Image Grid */}
      {assets.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 1.5 }}>
          {assets.map((asset, idx) => (
            <Box key={idx} onClick={() => asset.s3Url ? setGalLightbox(idx) : null} sx={{
              position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: asset.s3Url ? 'pointer' : 'default',
              aspectRatio: '1', bgcolor: isDark ? '#0f172a' : '#f8fafc',
              border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
              '&:hover .img-overlay': { opacity: 1 },
              '&:hover .img-zoom': { opacity: 1 },
              transition: 'all 0.2s ease',
              '&:hover': { borderColor: ASSET_COLOR, transform: 'translateY(-3px)', boxShadow: isDark ? `0 8px 20px rgba(14,165,233,0.15)` : `0 8px 20px rgba(14,165,233,0.12)` },
              contain: 'content',
            }}>
              {asset.s3Url ? (
                <Box component="img" src={asset.s3Url} alt={asset.fileName} loading="lazy" decoding="async"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1 }}>
                  <ImageIcon sx={{ fontSize: 36, color: isDark ? '#334155' : '#cbd5e1' }} />
                  <Typography variant="caption" noWrap sx={{ color: isDark ? '#475569' : '#94a3b8', fontSize: 9, px: 1, maxWidth: '100%' }}>{asset.fileName}</Typography>
                </Box>
              )}
              {/* Hover overlay */}
              <Box className="img-overlay" sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0, transition: 'opacity 0.25s ease',
                background: 'linear-gradient(transparent 0%, rgba(0,0,0,0.85) 100%)', p: 1.5, pt: 4,
              }}>
                <Typography variant="caption" noWrap sx={{ color: '#fff', fontSize: 11, display: 'block', fontWeight: 600, letterSpacing: 0.2 }}>
                  {asset.fileName}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, display: 'block', mt: 0.25 }}>
                  {asset.sku || asset.collection || ''}
                </Typography>
              </Box>
              {/* Zoom icon on hover */}
              {asset.s3Url && (
                <Box className="img-zoom" sx={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  opacity: 0, transition: 'opacity 0.2s ease',
                  width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ZoomInIcon sx={{ color: '#fff', fontSize: 18 }} />
                </Box>
              )}
              {/* View type badge */}
              <Chip label={asset.viewType || '?'} size="small" sx={{
                position: 'absolute', top: 6, right: 6,
                bgcolor: getViewTypeColor(asset.viewType) + 'E6', color: '#fff', fontSize: 9, height: 20, fontWeight: 600,
                backdropFilter: 'blur(4px)', letterSpacing: 0.3,
              }} />
            </Box>
          ))}
        </Box>
      ) : !loading && (
        <Box sx={{ textAlign: 'center', py: 10, color: isDark ? '#64748b' : '#94a3b8' }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: isDark ? '#1e293b' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <ImageIcon sx={{ fontSize: 36, opacity: 0.4 }} />
          </Box>
          <Typography variant="body1" fontWeight={500}>No assets found</Typography>
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>Try a different search term or adjust filters</Typography>
        </Box>
      )}

      {/* Bottom pagination */}
      {assets.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <TablePagination
            component="div"
            count={totalResults}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { const v = parseInt(e.target.value, 10); setRowsPerPage(v); setPage(1); searchAssets(searchQuery, categoryFilter, viewTypeFilter, 1, v); }}
            rowsPerPageOptions={[50, 100, 200]}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
          />
        </Box>
      )}

      {/* Gallery Lightbox Dialog */}
      <Dialog open={galLightbox >= 0} onClose={() => { setGalLightbox(-1); setGalZoom(false); }}
        maxWidth="xl" fullWidth
        PaperProps={{ sx: { bgcolor: isDark ? '#0f172a' : '#fff', maxHeight: '95vh', overflow: 'hidden' } }}>
        {galLightbox >= 0 && galLightbox < assets.length && (() => {
          const img = assets[galLightbox];
          return (
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>
              {/* Left: Image area */}
              <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? '#000' : '#f1f5f9', overflow: 'hidden', cursor: galZoom ? 'zoom-out' : 'zoom-in', minHeight: 400, maxHeight: '90vh' }}
                onClick={(e) => { if (!galZoom) { setGalZoom(true); e.stopPropagation(); } else setGalZoom(false); }}
                onMouseMove={(e) => {
                  if (!galZoom) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  const overlay = e.currentTarget.querySelector('.zoom-overlay');
                  if (overlay) { overlay.style.opacity = '1'; overlay.style.backgroundPosition = `${x}% ${y}%`; }
                }}
              >
                {img.s3Url ? (
                  <>
                    <Box component="img" src={img.s3Url} alt={img.fileName}
                      sx={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', userSelect: 'none', display: 'block' }} />
                    {galZoom && (
                      <div className="zoom-overlay" style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundImage: `url(${img.s3Url})`,
                        backgroundSize: '250%', backgroundRepeat: 'no-repeat',
                        backgroundPosition: '50% 50%',
                        opacity: 0, pointerEvents: 'none',
                        transition: 'opacity 0.1s',
                      }}
                      ref={(el) => {
                        if (!el) return;
                        const parent = el.parentElement;
                        parent.onmousemove = (e) => {
                          const rect = parent.getBoundingClientRect();
                          const x = ((e.clientX - rect.left) / rect.width) * 100;
                          const y = ((e.clientY - rect.top) / rect.height) * 100;
                          el.style.opacity = '1';
                          el.style.backgroundPosition = `${x}% ${y}%`;
                        };
                      }}
                      />
                    )}
                    {!galZoom && (
                      <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', bgcolor: 'rgba(0,0,0,0.6)', borderRadius: 2, px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, backdropFilter: 'blur(4px)' }}>
                        <ZoomInIcon sx={{ fontSize: 16, color: '#fff' }} />
                        <Typography variant="caption" sx={{ color: '#fff', fontSize: 11, fontWeight: 500 }}>Click to zoom</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <ImageIcon sx={{ fontSize: 80, color: isDark ? '#334155' : '#cbd5e1' }} />
                )}
                {/* Nav arrows */}
                {galLightbox > 0 && (
                  <IconButton onClick={(e) => { e.stopPropagation(); setGalLightbox(galLightbox - 1); setGalZoom(false); }}
                    sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                    <ChevronLeftIcon />
                  </IconButton>
                )}
                {galLightbox < assets.length - 1 && (
                  <IconButton onClick={(e) => { e.stopPropagation(); setGalLightbox(galLightbox + 1); setGalZoom(false); }}
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                    <ChevronRightIcon />
                  </IconButton>
                )}
                {/* Counter */}
                <Typography sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                  {galLightbox + 1} / {assets.length}
                </Typography>
              </Box>

              {/* Right: Sidebar */}
              <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, borderLeft: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, p: 2.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '90vh' }}>
                {/* Close */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton size="small" onClick={() => { setGalLightbox(-1); setGalZoom(false); }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                </Box>
                {/* File name */}
                <Typography variant="body2" fontWeight={600} sx={{ color: isDark ? '#f8fafc' : '#0f172a', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 13 }}>
                  {img.fileName}
                </Typography>
                <Divider sx={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }} />
                {/* Metadata */}
                {img.viewType && (
                  <Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase' }}>View Type</Typography>
                    <Chip label={img.viewType} size="small" sx={{ mt: 0.5, bgcolor: getViewTypeColor(img.viewType) + '20', color: getViewTypeColor(img.viewType), fontWeight: 600, fontSize: 11 }} />
                  </Box>
                )}
                {img.sku && (
                  <Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase' }}>SKU</Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#334155', fontFamily: 'monospace', fontSize: 12 }}>{img.sku}</Typography>
                  </Box>
                )}
                {img.collection && (
                  <Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase' }}>Collection</Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#334155', fontSize: 12 }}>{img.collection}</Typography>
                  </Box>
                )}
                {img.category && (
                  <Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase' }}>Category</Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#334155', fontSize: 12 }}>{img.category}</Typography>
                  </Box>
                )}
                <Divider sx={{ borderColor: isDark ? '#1e293b' : '#e2e8f0' }} />
                {/* Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {img.s3Url && (
                    <>
                      <Button fullWidth size="small" variant="contained" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                        onClick={() => downloadAsset(img.s3Url, img.fileName)}
                        sx={{ textTransform: 'none', fontSize: 12, bgcolor: ASSET_COLOR, '&:hover': { bgcolor: ASSET_HOVER } }}>
                        Download
                      </Button>
                      <Button fullWidth size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                        onClick={() => window.open(img.s3Url, '_blank')}
                        sx={{ textTransform: 'none', fontSize: 12, borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Open S3 Link
                      </Button>
                      <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                        onClick={() => { navigator.clipboard.writeText(img.s3Url); enqueueSnackbar('S3 URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 }); }}
                        sx={{ textTransform: 'none', fontSize: 12, borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Copy S3 URL
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </DialogContent>
          );
        })()}
      </Dialog>
    </Box>
  );

  // =========================================================================
  // Table View
  // =========================================================================
  const TableView = () => (
    <TableContainer component={Paper} sx={{
      bgcolor: isDark ? '#1e293b' : '#fff',
      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
      borderRadius: 2,
      maxHeight: 600,
    }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {['fileName', 'sku', 'collection', 'viewType', 'category'].map(col => (
              <TableCell
                key={col}
                sortDirection={orderBy === col ? order : false}
                sx={{
                  bgcolor: isDark ? '#0f172a' : '#f8fafc',
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                  borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                }}
              >
                <TableSortLabel
                  active={orderBy === col}
                  direction={orderBy === col ? order : 'asc'}
                  onClick={() => handleSort(col)}
                  sx={{ '& .MuiTableSortLabel-icon': { color: `${ASSET_COLOR} !important` } }}
                >
                  {col === 'fileName' ? 'File Name' : col === 'sku' ? 'SKU' : col === 'viewType' ? 'View Type' : col.charAt(0).toUpperCase() + col.slice(1)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={{ bgcolor: isDark ? '#0f172a' : '#f8fafc', borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, width: 40 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {assets.map((asset, i) => (
            <TableRow key={i} hover sx={{
              '&:hover': { bgcolor: isDark ? '#1e293b80' : '#f8fafc' },
              cursor: 'pointer',
            }}>
              <TableCell sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {asset.fileName}
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12, fontFamily: 'monospace' }}>
                {asset.sku || '-'}
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                {asset.collection || '-'}
              </TableCell>
              <TableCell>
                <Chip
                  label={asset.viewType || 'Unknown'}
                  size="small"
                  sx={{
                    bgcolor: getViewTypeColor(asset.viewType) + '20',
                    color: getViewTypeColor(asset.viewType),
                    fontSize: 10, height: 22, fontWeight: 600,
                    border: `1px solid ${getViewTypeColor(asset.viewType)}40`,
                  }}
                />
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 12 }}>
                {asset.category || '-'}
              </TableCell>
              <TableCell>
                {asset.s3Url && (
                  <Tooltip title="Open image">
                    <IconButton size="small" onClick={() => window.open(asset.s3Url, '_blank')} sx={{ color: ASSET_COLOR }}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // =========================================================================
  // Graph View — Interactive, expandable tree graph
  // =========================================================================
  const NODE_COLORS = { category: '#f59e0b', collection: '#3b82f6', product: '#10b981', variant: '#8b5cf6', sku: '#ec4899', asset: '#0ea5e9' };
  const NODE_SIZES = { category: 28, collection: 22, product: 16, variant: 13, sku: 11, asset: 8 };
  const NODE_LABELS = { category: 'Category', collection: 'Collection', product: 'Product', variant: 'Variant', sku: 'SKU', asset: 'Asset' };
  const CHILD_TYPE = { category: 'collection', collection: 'product', product: 'variant', variant: 'sku', sku: 'asset' };

  // Load root nodes once
  useEffect(() => { if (!gInitialized) { loadGraphRoot(); setGInitialized(true); } }, [gInitialized]);

  // Sync main search to graph when tab is active
  useEffect(() => { if (searchQuery && activeTab === 2) { setGSearch(searchQuery); searchGraphNodes(searchQuery); } }, [searchQuery, activeTab]);

  // Autocomplete — only after 500ms pause, minimum 3 chars
  useEffect(() => {
    if (gSuggestTimeout.current) clearTimeout(gSuggestTimeout.current);
    if (!gSearch || gSearch.length < 3) { setGSuggestions([]); setGShowSuggestions(false); return; }
    gSuggestTimeout.current = setTimeout(async () => {
      try { const d = await apiCall(`${API_ENDPOINTS.ASSET_EXPLORER_SUGGEST}?q=${encodeURIComponent(gSearch)}`); setGSuggestions(d.suggestions || []); setGShowSuggestions(true); } catch { setGSuggestions([]); }
    }, 500);
    return () => clearTimeout(gSuggestTimeout.current);
  }, [gSearch]);

  // Collect asset images from visible nodes
  useEffect(() => {
    const images = gNodes.filter(n => n.type === 'asset' && n.s3Url).map(n => ({ fileName: n.label, s3Url: n.s3Url, viewType: n.viewType || '' }));
    setGAssetImages(images);
    if (images.length > 0 && images.length <= 50) setGImagePanel(true);
    else setGImagePanel(false);
  }, [gNodes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') { setGSelected(null); setGDetail(null); setGHighlighted(new Set()); }
      if ((e.key === 'Backspace' || (e.ctrlKey && e.key === 'z')) && activeTab === 2) { e.preventDefault(); graphPopHistory(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab]);

  const graphPushHistory = () => { setGHistory(prev => [...prev.slice(-19), { nodes: [...gNodes], links: [...gLinks], expanded: new Set(gExpanded), breadcrumbs: [...gBreadcrumbs] }]); };
  const graphPopHistory = () => {
    setGHistory(prev => {
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      setGNodes(last.nodes); setGLinks(last.links); setGExpanded(last.expanded); setGBreadcrumbs(last.breadcrumbs);
      return prev.slice(0, -1);
    });
  };

  const loadGraphRoot = async () => {
    setGLoading(true);
    try {
      const data = await apiCall(`${API_ENDPOINTS.ASSET_EXPLORER_EXPAND}?nodeType=root`);
      const children = (data && data.children) ? data.children.filter(c => c && c.id) : [];
      setGNodes(children.map((c, i) => ({ ...c, x: 200, y: 60 + i * 55, depth: 0, parentId: null })));
      setGLinks([]); setGBreadcrumbs([]); setGSelected(null); setGDetail(null); setGHighlighted(new Set()); setGHistory([]);
    } catch (err) { console.error('Graph root load error:', err); }
    finally { setGLoading(false); }
  };

  const searchGraphNodes = async (q) => {
    if (!q || q.length < 2) return;
    graphPushHistory(); setGLoading(true);
    try {
      const data = await apiCall(`${API_ENDPOINTS.ASSET_EXPLORER_EXPAND}?nodeType=search&q=${encodeURIComponent(q)}`);
      if (data && data.children && data.children.length) {
        const allNodes = []; const allLinks = []; const expandedSet = new Set(); let y = 40;
        data.children.forEach((group) => {
          if (!group || !group.id) return;
          allNodes.push({ ...group, children: undefined, x: 200, y, depth: 0, expanded: true, parentId: null });
          expandedSet.add(group.id);
          const childList = (group.children || []).filter(c => c && c.id);
          childList.forEach((child, ci) => {
            const childY = y + ci * 36;
            allNodes.push({ ...child, children: undefined, x: 420, y: childY, depth: 1, expanded: false, parentId: group.id });
            allLinks.push({ source: group.id, target: child.id });
          });
          y += Math.max(childList.length * 36 + 20, 60);
        });
        setGNodes(allNodes); setGLinks(allLinks);
        setGExpanded(expandedSet);
        setGBreadcrumbs([{ id: 'search', label: `Search: "${q}"`, type: 'category' }]);
        setGHighlighted(new Set(allNodes.filter(n => n.type === 'asset' || n.depth === 1).map(n => n.id)));
        setGPan({ x: 20, y: 0 }); setGZoom(0.8);
      } else { setGNodes([]); setGLinks([]); setGHighlighted(new Set()); }
    } catch (err) { console.error('Graph search error:', err); setError(typeof err === 'string' ? err : (err?.message || 'Search failed')); }
    finally { setGLoading(false); setGShowSuggestions(false); }
  };

  const graphExpandFamily = async (node) => {
    // Load the parent SKU + all sibling assets for this asset node
    graphPushHistory(); setGLoading(true);
    try {
      const params = new URLSearchParams();
      if (node.parentSku) params.append('sku', node.parentSku);
      else params.append('fileName', node.nodeId || node.label);
      const data = await apiCall(`${API_ENDPOINTS.ASSET_EXPLORER_EXPAND_FAMILY}?${params.toString()}`);
      if (data && data.parent && data.siblings?.length) {
        const parentNode = { ...data.parent, x: node.x - 200, y: node.y, depth: node.depth, parentId: node.parentId };
        const siblingNodes = data.siblings.map((s, i) => ({
          ...s, x: node.x, y: node.y - (data.siblings.length * 32) / 2 + i * 32, depth: node.depth + 1, parentId: data.parent.id,
        }));
        const newLinks = siblingNodes.map(s => ({ source: data.parent.id, target: s.id }));
        // Also link parent to the original category if it exists
        if (node.parentId) newLinks.push({ source: node.parentId, target: data.parent.id });
        // Remove the original asset node (it's now in the siblings list)
        setGNodes(prev => [...prev.filter(n => n.id !== node.id), parentNode, ...siblingNodes]);
        setGLinks(prev => [...prev.filter(l => l.target !== node.id), ...newLinks]);
        setGExpanded(prev => new Set(prev).add(data.parent.id));
        setGSelected(node.id); // keep the original asset selected
        setGHighlighted(new Set([node.id])); // highlight the one they clicked
        setGBreadcrumbs(prev => [...prev, { id: data.parent.id, label: data.parent.label, type: 'sku' }]);
        setTimeout(() => setGHighlighted(new Set()), 3000);
      }
    } catch (err) { console.error('Expand family error:', err); }
    finally { setGLoading(false); }
  };

  const graphExpandNode = async (node) => {
    if (!CHILD_TYPE[node.type]) {
      // Asset node — expand its family (parent SKU + all siblings)
      if (node.type === 'asset') { graphExpandFamily(node); return; }
      if (node.s3Url) window.open(node.s3Url, '_blank');
      return;
    }
    const nodeId = node.id;
    graphPushHistory();
    if (gExpanded.has(nodeId)) {
      const toRemove = new Set();
      const findDesc = (id) => { gNodes.forEach(n => { if (n.parentId === id) { toRemove.add(n.id); findDesc(n.id); } }); };
      findDesc(nodeId);
      setGNodes(prev => prev.filter(n => !toRemove.has(n.id)));
      setGLinks(prev => prev.filter(l => !toRemove.has(l.target)));
      setGExpanded(prev => { const s = new Set(prev); s.delete(nodeId); toRemove.forEach(id => s.delete(id)); return s; });
      return;
    }
    setGLoading(true);
    try {
      const data = await apiCall(`${API_ENDPOINTS.ASSET_EXPLORER_EXPAND}?nodeType=${node.type}&nodeId=${encodeURIComponent(node.nodeId || node.label)}&parentId=${encodeURIComponent(node.parentNodeId || '')}`);
      const children = data.children || [];
      if (!children.length) { setGLoading(false); return; }
      const spacing = node.type === 'sku' || node.type === 'variant' ? 32 : 38;
      const startX = node.x + 200;
      const startY = node.y - (children.length * spacing) / 2;
      const newNodes = children.map((c, i) => ({ ...c, x: startX, y: startY + i * spacing, depth: node.depth + 1, parentId: nodeId }));
      const newLinks = children.map(c => ({ source: nodeId, target: c.id }));
      setGNodes(prev => [...prev, ...newNodes]); setGLinks(prev => [...prev, ...newLinks]);
      setGExpanded(prev => new Set(prev).add(nodeId));
      setGBreadcrumbs(prev => { const idx = prev.findIndex(b => b.id === nodeId); return idx >= 0 ? prev.slice(0, idx + 1) : [...prev, { id: nodeId, label: node.label, type: node.type }]; });
      setGHighlighted(new Set(newNodes.map(n => n.id)));
      setTimeout(() => setGHighlighted(new Set()), 2000);
    } catch (err) { console.error('Graph expand error:', err); setError(typeof err === 'string' ? err : (err?.message || 'Expand failed')); }
    finally { setGLoading(false); }
  };

  const handleSuggestionClick = (s) => {
    setGSearch(s.label); setGShowSuggestions(false);
    searchGraphNodes(s.nodeId || s.label);
  };
  const graphHandleNodeClick = (node) => { setGSelected(node.id); setGDetail(node); graphExpandNode(node); };
  const graphDblClick = (node, e) => { e.stopPropagation(); const cw = gContainerRef.current?.clientWidth || 900; const ch = gContainerRef.current?.clientHeight || 580; setGPan({ x: cw / 2 - node.x * gZoom, y: ch / 2 - node.y * gZoom }); };
  const graphWheel = (e) => { e.preventDefault(); setGZoom(prev => Math.max(0.15, Math.min(4, prev * (e.deltaY > 0 ? 0.9 : 1.1)))); };
  const graphMouseDown = (e) => { if (e.target.tagName === 'svg' || e.target.tagName === 'rect') gDragRef.current = { startX: e.clientX - gPan.x, startY: e.clientY - gPan.y }; };
  const graphMouseMove = (e) => { if (gDragRef.current) setGPan({ x: e.clientX - gDragRef.current.startX, y: e.clientY - gDragRef.current.startY }); };
  const graphMouseUp = () => { gDragRef.current = null; };

  // Lightbox helpers
  const openLightbox = (images, index) => { setLightboxImages(images); setLightboxIndex(index); setLightboxOpen(true); };
  const closeLightbox = () => { setLightboxOpen(false); };
  const lightboxPrev = () => setLightboxIndex(i => (i - 1 + lightboxImages.length) % lightboxImages.length);
  const lightboxNext = () => setLightboxIndex(i => (i + 1) % lightboxImages.length);

  // Lightbox keyboard nav
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, lightboxImages.length]);

  const GraphView = () => {
    const visibleNodes = gTypeFilter ? gNodes.filter(n => n.type === gTypeFilter || gNodes.some(cn => cn.parentId === n.id && cn.type === gTypeFilter)) : gNodes;
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    const visibleLinks = gLinks.filter(l => visibleNodeIds.has(l.source) && visibleNodeIds.has(l.target));
    const typeCounts = {};
    gNodes.forEach(n => { typeCounts[n.type] = (typeCounts[n.type] || 0) + 1; });

    return (
      <Box>
        {/* Graph Search + Controls */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <TextField fullWidth size="small" placeholder="Search graph — SKU, collection, view type..."
              value={gSearch} onChange={(e) => setGSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { searchGraphNodes(gSearch); setGShowSuggestions(false); } if (e.key === 'Escape') setGShowSuggestions(false); }}
              onFocus={() => { if (gSuggestions.length) setGShowSuggestions(true); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: ASSET_COLOR, fontSize: 20 }} /></InputAdornment>,
                endAdornment: gSearch && <InputAdornment position="end"><IconButton size="small" onClick={() => { setGSearch(''); setGHighlighted(new Set()); loadGraphRoot(); }}><CloseIcon sx={{ fontSize: 16, color: isDark ? '#475569' : '#cbd5e1' }} /></IconButton></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: isDark ? '#1e293b' : '#fff', borderRadius: 2.5, '&.Mui-focused': { boxShadow: `0 0 0 3px ${ASSET_COLOR}20` }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ASSET_COLOR } } }}
            />
            {gShowSuggestions && gSuggestions.length > 0 && (
              <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, mt: 0.5, bgcolor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 2, maxHeight: 280, overflowY: 'auto', boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.1)' }}>
                {gSuggestions.map((s, i) => (
                  <Box key={i} onClick={() => handleSuggestionClick(s)} sx={{ px: 2, py: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1.5, transition: 'background 0.1s', '&:hover': { bgcolor: isDark ? '#334155' : '#f1f5f9' }, borderBottom: i < gSuggestions.length - 1 ? `1px solid ${isDark ? '#1e293b' : '#f1f5f9'}` : 'none' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: NODE_COLORS[s.type] || '#6b7280', flexShrink: 0, boxShadow: `0 0 6px ${NODE_COLORS[s.type] || '#6b7280'}50` }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: 13, fontWeight: 500 }}>{s.label}</Typography>
                      <Typography variant="caption" noWrap sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10 }}>{NODE_LABELS[s.type] || s.type} {s.count ? `• ${s.count} assets` : ''}</Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
          </Box>
          <Tooltip title="Search graph" arrow><IconButton onClick={() => searchGraphNodes(gSearch)} sx={{ bgcolor: ASSET_COLOR, color: '#fff', borderRadius: 2, '&:hover': { bgcolor: ASSET_HOVER }, width: 38, height: 38, boxShadow: `0 2px 8px ${ASSET_COLOR}40` }}><SearchIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Undo (Ctrl+Z)" arrow><span><IconButton onClick={graphPopHistory} disabled={!gHistory.length} sx={{ bgcolor: isDark ? '#1e293b' : '#f1f5f9', borderRadius: 2, width: 38, height: 38, '&:hover': { bgcolor: isDark ? '#334155' : '#e2e8f0' } }}><Typography sx={{ fontSize: 16 }}>↩</Typography></IconButton></span></Tooltip>
          <Tooltip title="Reset" arrow><IconButton onClick={loadGraphRoot} sx={{ bgcolor: isDark ? '#1e293b' : '#f1f5f9', borderRadius: 2, width: 38, height: 38, '&:hover': { bgcolor: isDark ? '#334155' : '#e2e8f0' } }}><RefreshIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        </Box>

        {/* Filters + Breadcrumbs bar */}
        <Paper sx={{ display: 'flex', gap: 0.75, mb: 1.5, alignItems: 'center', flexWrap: 'wrap', px: 1.5, py: 1, bgcolor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 2.5 }}>
          {Object.entries(typeCounts).map(([type, count]) => (
            <Chip key={type} label={`${NODE_LABELS[type] || type} ${count}`} size="small"
              onClick={() => setGTypeFilter(gTypeFilter === type ? '' : type)}
              sx={{ bgcolor: gTypeFilter === type ? NODE_COLORS[type] : `${NODE_COLORS[type]}12`, color: gTypeFilter === type ? '#fff' : NODE_COLORS[type], border: `1px solid ${NODE_COLORS[type]}30`, fontSize: 10, height: 26, cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s', boxShadow: gTypeFilter === type ? `0 2px 6px ${NODE_COLORS[type]}40` : 'none', '&:hover': { transform: 'translateY(-1px)' } }}
            />
          ))}
          {gTypeFilter && <Chip label="Clear" size="small" variant="outlined" onClick={() => setGTypeFilter('')} sx={{ fontSize: 10, height: 26, cursor: 'pointer', borderStyle: 'dashed' }} />}
          {gBreadcrumbs.length > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
          {gBreadcrumbs.length > 0 && (
            <>
              <Chip label="⌂" size="small" onClick={loadGraphRoot} sx={{ bgcolor: isDark ? '#0f172a' : '#f1f5f9', cursor: 'pointer', fontSize: 12, height: 26, '&:hover': { bgcolor: isDark ? '#334155' : '#e2e8f0' } }} />
              {gBreadcrumbs.map((b) => (
                <React.Fragment key={b.id}>
                  <Typography sx={{ color: isDark ? '#475569' : '#cbd5e1', fontSize: 11, mx: -0.25 }}>›</Typography>
                  <Chip label={b.label} size="small" sx={{ bgcolor: `${NODE_COLORS[b.type]}18`, color: NODE_COLORS[b.type], fontSize: 10, height: 26, fontWeight: 600, border: `1px solid ${NODE_COLORS[b.type]}30` }} />
                </React.Fragment>
              ))}
            </>
          )}
          {gLoading && <CircularProgress size={14} sx={{ ml: 'auto', color: ASSET_COLOR }} />}
          {!gLoading && <Typography variant="caption" sx={{ ml: 'auto', color: isDark ? '#475569' : '#94a3b8', fontSize: 10 }}>{visibleNodes.length} nodes</Typography>}
        </Paper>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {/* Graph Canvas */}
          <Paper ref={gContainerRef} sx={{
            flex: 1, bgcolor: isDark ? '#0a0f1a' : '#fafbfc',
            border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
            borderRadius: 3, overflow: 'hidden', height: 600, position: 'relative', cursor: gDragRef.current ? 'grabbing' : 'grab',
            backgroundImage: isDark
              ? 'radial-gradient(circle at 1px 1px, #1e293b20 1px, transparent 0)'
              : 'radial-gradient(circle at 1px 1px, #e2e8f010 1px, transparent 0)',
            backgroundSize: '24px 24px',
          }}
            onWheel={graphWheel} onMouseDown={graphMouseDown} onMouseMove={graphMouseMove} onMouseUp={graphMouseUp} onMouseLeave={graphMouseUp}
          >
            {visibleNodes.length === 0 && !gLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: isDark ? '#1e293b' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BubbleChartIcon sx={{ fontSize: 36, color: isDark ? '#334155' : '#cbd5e1' }} />
                </Box>
                <Typography sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 14, fontWeight: 500 }}>
                  {gSearch ? 'No results found' : 'Loading graph...'}
                </Typography>
                {gSearch && <Typography variant="caption" sx={{ color: isDark ? '#475569' : '#cbd5e1' }}>Try a different search term</Typography>}
              </Box>
            ) : (
              <svg width="100%" height="100%" style={{ display: 'block', fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif' }}>
                <defs>
                  <filter id="nodeShadow"><feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000" floodOpacity="0.12" /></filter>
                </defs>
                <g transform={`translate(${gPan.x}, ${gPan.y}) scale(${gZoom})`}>
                  {/* Links — clean bezier curves */}
                  {visibleLinks.map((link, i) => {
                    const s = visibleNodes.find(n => n.id === link.source);
                    const t = visibleNodes.find(n => n.id === link.target);
                    if (!s || !t) return null;
                    const cp = Math.max(50, Math.abs(t.x - s.x) * 0.45);
                    const isActive = gSelected && (gSelected === link.source || gSelected === link.target);
                    const isHighlighted = gHighlighted.has(link.target) || gHighlighted.has(link.source);
                    const color = isHighlighted ? ASSET_COLOR : isActive ? (NODE_COLORS[t.type] || '#64748b') : (isDark ? '#334155' : '#d1d5db');
                    return (
                      <path key={i}
                        d={`M${s.x},${s.y} C${s.x + cp},${s.y} ${t.x - cp},${t.y} ${t.x},${t.y}`}
                        fill="none" stroke={color}
                        strokeWidth={isHighlighted ? 2 : isActive ? 1.5 : 1}
                        strokeOpacity={isHighlighted ? 0.8 : isActive ? 0.6 : 0.25}
                        strokeLinecap="round"
                      />
                    );
                  })}
                  {/* Nodes — rounded rect cards */}
                  {visibleNodes.map((node) => {
                    const color = NODE_COLORS[node.type] || '#6b7280';
                    const isSelected = gSelected === node.id;
                    const isHovered = gHovered === node.id;
                    const isExpanded = gExpanded.has(node.id);
                    const hasChildren = !!CHILD_TYPE[node.type];
                    const isHighlighted = gHighlighted.has(node.id);
                    const isConnected = gSelected && gLinks.some(l => (l.source === gSelected && l.target === node.id) || (l.target === gSelected && l.source === node.id));
                    const dimmed = (gSelected && !isSelected && !isConnected && node.parentId !== gSelected) || (gTypeFilter && node.type !== gTypeFilter);

                    // Card dimensions based on type
                    const isLarge = node.type === 'category';
                    const isMed = node.type === 'collection' || node.type === 'sku';
                    const w = isLarge ? 160 : isMed ? 140 : 130;
                    const h = isLarge ? 36 : isMed ? 30 : 26;
                    const r = isLarge ? 8 : 6;
                    const fs = isLarge ? 12 : isMed ? 11 : 10;
                    const dotR = isLarge ? 6 : isMed ? 5 : 4;
                    const label = (node.label || '').length > 20 ? (node.label || '').slice(0, 18) + '...' : (node.label || '');
                    const countStr = node.count ? ` (${node.count})` : '';

                    return (
                      <g key={node.id}
                        onClick={(e) => { e.stopPropagation(); graphHandleNodeClick(node); }}
                        onDoubleClick={(e) => graphDblClick(node, e)}
                        onMouseEnter={() => setGHovered(node.id)}
                        onMouseLeave={() => setGHovered(null)}
                        style={{ cursor: 'pointer' }}
                        opacity={dimmed ? 0.12 : 1}
                      >
                        {/* Highlight ring */}
                        {isHighlighted && (
                          <rect x={node.x - w/2 - 4} y={node.y - h/2 - 4} width={w + 8} height={h + 8} rx={r + 2}
                            fill="none" stroke={ASSET_COLOR} strokeWidth={2} strokeOpacity={0.5} strokeDasharray="6 3">
                            <animate attributeName="stroke-dashoffset" from="0" to="18" dur="1s" repeatCount="indefinite" />
                          </rect>
                        )}
                        {/* Card background */}
                        <rect x={node.x - w/2} y={node.y - h/2} width={w} height={h} rx={r}
                          fill={isDark ? '#1e293b' : '#ffffff'}
                          stroke={isSelected ? color : isHovered ? color : (isDark ? '#334155' : '#e2e8f0')}
                          strokeWidth={isSelected ? 2 : isHovered ? 1.5 : 1}
                          filter="url(#nodeShadow)"
                          style={{ transition: 'stroke 0.15s' }}
                        />
                        {/* Left color accent bar */}
                        <rect x={node.x - w/2} y={node.y - h/2} width={4} height={h} rx={2}
                          fill={color}
                        />
                        {/* Color dot */}
                        <circle cx={node.x - w/2 + 16} cy={node.y} r={dotR} fill={color} />
                        {/* Label */}
                        <text x={node.x - w/2 + 16 + dotR + 6} y={node.y + fs * 0.35}
                          fill={isDark ? '#e2e8f0' : '#1e293b'}
                          fontSize={fs} fontWeight={isSelected ? 700 : isLarge ? 600 : 500}
                          style={{ pointerEvents: 'none' }}>
                          {label}{countStr}
                        </text>
                        {/* Expand/collapse indicator on right */}
                        {hasChildren && (
                          <g style={{ pointerEvents: 'none' }}>
                            <circle cx={node.x + w/2 - 14} cy={node.y} r={8}
                              fill={isExpanded ? color : (isDark ? '#0f172a' : '#f1f5f9')}
                              stroke={color} strokeWidth={1} />
                            <line x1={node.x + w/2 - 18} y1={node.y} x2={node.x + w/2 - 10} y2={node.y}
                              stroke={isExpanded ? '#fff' : color} strokeWidth={1.5} strokeLinecap="round" />
                            {!isExpanded && <line x1={node.x + w/2 - 14} y1={node.y - 4} x2={node.x + w/2 - 14} y2={node.y + 4}
                              stroke={color} strokeWidth={1.5} strokeLinecap="round" />}
                          </g>
                        )}
                        {/* Type label for asset nodes */}
                        {node.type === 'asset' && node.viewType && (
                          <text x={node.x + w/2 - 6} y={node.y + fs * 0.35} textAnchor="end"
                            fill={getViewTypeColor(node.viewType)} fontSize={8} fontWeight={600}
                            style={{ pointerEvents: 'none', textTransform: 'uppercase' }}>
                            {node.viewType}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              </svg>
            )}

            {/* Node count overlay */}
            <Box sx={{ position: 'absolute', top: 12, left: 12, bgcolor: isDark ? '#0a0f1aE6' : '#ffffffE6', borderRadius: 2, px: 2, py: 0.75, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, backdropFilter: 'blur(8px)' }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 11, fontWeight: 500 }}>
                {visibleNodes.length} nodes {visibleLinks.length > 0 && `• ${visibleLinks.length} edges`}{gHighlighted.size > 0 ? ` • ${gHighlighted.size} highlighted` : ''}
              </Typography>
            </Box>

            {/* Zoom controls */}
            <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: isDark ? '#0a0f1aE6' : '#ffffffE6', borderRadius: 2.5, p: 0.5, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, backdropFilter: 'blur(8px)' }}>
              {[
                { label: '+', tip: 'Zoom in', action: () => setGZoom(z => Math.min(4, z * 1.25)) },
                { label: '−', tip: 'Zoom out', action: () => setGZoom(z => Math.max(0.15, z * 0.8)) },
                { label: '⟲', tip: 'Reset view', action: () => { setGPan({ x: 0, y: 0 }); setGZoom(1); } },
                { label: '⛶', tip: 'Fit to content', action: () => { const xs = visibleNodes.map(n => n.x); const ys = visibleNodes.map(n => n.y); if (!xs.length) return; const cx = (Math.min(...xs) + Math.max(...xs)) / 2; const cy = (Math.min(...ys) + Math.max(...ys)) / 2; const cw = gContainerRef.current?.clientWidth || 900; const ch = gContainerRef.current?.clientHeight || 600; setGPan({ x: cw / 2 - cx, y: ch / 2 - cy }); setGZoom(1); } },
              ].map((btn, i) => (
                <Tooltip key={i} title={btn.tip} placement="left" arrow>
                  <IconButton size="small" onClick={btn.action} sx={{ width: 32, height: 32, borderRadius: 1.5, '&:hover': { bgcolor: isDark ? '#1e293b' : '#f1f5f9' } }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? '#e2e8f0' : '#334155' }}>{btn.label}</Typography>
                  </IconButton>
                </Tooltip>
              ))}
              <Divider sx={{ mx: 0.5, borderColor: isDark ? '#1e293b' : '#e2e8f0' }} />
              <Typography variant="caption" sx={{ textAlign: 'center', color: isDark ? '#475569' : '#94a3b8', fontSize: 9, py: 0.25 }}>{Math.round(gZoom * 100)}%</Typography>
            </Box>
          </Paper>

          {/* Detail Panel */}
          <Paper sx={{
            width: 300, bgcolor: isDark ? '#111827' : '#fff',
            border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
            borderRadius: 3, height: 600, overflowY: 'auto', display: 'flex', flexDirection: 'column',
          }}>
            {gDetail ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header with gradient */}
                <Box sx={{ background: `linear-gradient(135deg, ${NODE_COLORS[gDetail.type]}20, ${NODE_COLORS[gDetail.type]}08)`, p: 2, borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: NODE_COLORS[gDetail.type], display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${NODE_COLORS[gDetail.type]}40` }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{(NODE_LABELS[gDetail.type] || '?')[0]}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontSize: 14 }}>
                        {gDetail.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 500 }}>
                        {NODE_LABELS[gDetail.type] || gDetail.type}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: 2, flex: 1 }}>

                {/* Stats */}
                {gDetail.count && (
                  <Box sx={{ bgcolor: isDark ? '#0a0f1a' : '#f8fafc', borderRadius: 2, p: 1.5, mb: 1.5, border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                    <Typography variant="h4" fontWeight={800} sx={{ color: NODE_COLORS[gDetail.type], lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                      {parseInt(gDetail.count).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 500 }}>
                      {CHILD_TYPE[gDetail.type] ? `${CHILD_TYPE[gDetail.type]}s` : 'items'}
                    </Typography>
                  </Box>
                )}

                {/* Image preview */}
                {gDetail.s3Url && (
                  <Box sx={{ mb: 1.5, borderRadius: 1, overflow: 'hidden', bgcolor: isDark ? '#0f172a' : '#f8fafc', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, cursor: 'pointer' }}
                    onClick={() => window.open(gDetail.s3Url, '_blank')}>
                    <Box component="img" src={gDetail.s3Url} alt={gDetail.label}
                      sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </Box>
                )}

                {/* View type badge */}
                {gDetail.viewType && (
                  <Chip label={gDetail.viewType} size="small" sx={{ bgcolor: getViewTypeColor(gDetail.viewType) + '20', color: getViewTypeColor(gDetail.viewType), fontSize: 10, mb: 1.5, fontWeight: 600 }} />
                )}

                {/* Connected nodes */}
                {(() => {
                  const children = gNodes.filter(n => n.parentId === gDetail.id);
                  const parent = gNodes.find(n => n.id === gDetail.parentId);
                  if (!children.length && !parent) return null;
                  return (
                    <Box sx={{ mt: 1 }}>
                      {parent && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Parent</Typography>
                          <Chip label={parent.label} size="small" onClick={() => graphHandleNodeClick(parent)}
                            sx={{ mt: 0.5, bgcolor: NODE_COLORS[parent.type] + '15', color: NODE_COLORS[parent.type], fontSize: 10, cursor: 'pointer', border: `1px solid ${NODE_COLORS[parent.type]}30` }} />
                        </Box>
                      )}
                      {children.length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Children ({children.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, maxHeight: 150, overflowY: 'auto' }}>
                            {children.slice(0, 30).map(c => (
                              <Chip key={c.id} label={c.label} size="small" onClick={() => graphHandleNodeClick(c)}
                                sx={{ bgcolor: NODE_COLORS[c.type] + '15', color: NODE_COLORS[c.type], fontSize: 9, cursor: 'pointer', border: `1px solid ${NODE_COLORS[c.type]}20`, height: 22 }} />
                            ))}
                            {children.length > 30 && <Typography variant="caption" sx={{ color: isDark ? '#475569' : '#cbd5e1', fontSize: 10 }}>+{children.length - 30} more</Typography>}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}

                {/* Actions */}
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {CHILD_TYPE[gDetail.type] && (
                    <Button size="small" fullWidth variant="outlined" onClick={() => graphExpandNode(gDetail)}
                      sx={{ textTransform: 'none', fontSize: 11, borderColor: NODE_COLORS[gDetail.type] + '40', color: NODE_COLORS[gDetail.type] }}>
                      {gExpanded.has(gDetail.id) ? 'Collapse' : `Expand ${CHILD_TYPE[gDetail.type]}s`}
                    </Button>
                  )}
                  <Button size="small" fullWidth variant="outlined" onClick={() => { setGSearch(gDetail.nodeId || gDetail.label); searchGraphNodes(gDetail.nodeId || gDetail.label); }}
                    sx={{ textTransform: 'none', fontSize: 11, borderColor: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#94a3b8' : '#64748b' }}>
                    Search for "{gDetail.label}"
                  </Button>
                  {gDetail.s3Url && (
                    <Button size="small" fullWidth variant="outlined" onClick={() => window.open(gDetail.s3Url, '_blank')}
                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      sx={{ textTransform: 'none', fontSize: 11, borderColor: isDark ? '#334155' : '#e2e8f0', color: ASSET_COLOR }}>
                      Open Image
                    </Button>
                  )}
                </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', p: 3 }}>
                <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: isDark ? '#1e293b' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                  <BubbleChartIcon sx={{ fontSize: 28, color: isDark ? '#334155' : '#cbd5e1' }} />
                </Box>
                <Typography variant="body2" sx={{ color: isDark ? '#475569' : '#94a3b8', textAlign: 'center', fontSize: 13, fontWeight: 500 }}>
                  Click a node to inspect
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#334155' : '#cbd5e1', textAlign: 'center', mt: 0.5, lineHeight: 1.5 }}>
                  Double-click to center<br />Esc to deselect
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap', alignItems: 'center', px: 1 }}>
          {Object.entries(NODE_COLORS).map(([type, color]) => (
            <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 4px ${color}50` }} />
              <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', textTransform: 'capitalize', fontSize: 10, fontWeight: 500 }}>{type}</Typography>
            </Box>
          ))}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
            {['Click = expand', 'Dbl-click = center', 'Scroll = zoom', 'Drag = pan'].map(t => (
              <Typography key={t} variant="caption" sx={{ color: isDark ? '#334155' : '#cbd5e1', fontSize: 9, fontWeight: 500 }}>{t}</Typography>
            ))}
          </Box>
        </Box>

        {/* Image Preview Panel — shows when assets are visible in graph (<=50) */}
        {gAssetImages.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Asset Images ({gAssetImages.length}) — click to preview
              </Typography>
              <IconButton size="small" onClick={() => setGImagePanel(!gImagePanel)} sx={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                {gImagePanel ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>
            <Collapse in={gImagePanel}>
              <Grid container spacing={1}>
                {gAssetImages.map((img, i) => (
                  <Grid item xs={4} sm={3} md={2} key={i}>
                    <Box sx={{
                      borderRadius: 1, overflow: 'hidden', cursor: 'pointer',
                      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                      bgcolor: isDark ? '#1e293b' : '#fff',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: ASSET_COLOR, transform: 'translateY(-1px)', boxShadow: `0 2px 8px ${ASSET_COLOR}20` },
                    }} onClick={() => openLightbox(gAssetImages, i)}>
                      <Box sx={{ height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? '#0f172a' : '#f8fafc', position: 'relative' }}>
                        <Box component="img" src={img.s3Url} alt={img.fileName} loading="lazy"
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; }} />
                        {img.viewType && (
                          <Chip label={img.viewType} size="small" sx={{ position: 'absolute', top: 3, right: 3, bgcolor: getViewTypeColor(img.viewType) + 'DD', color: '#fff', fontSize: 8, height: 16, fontWeight: 600 }} />
                        )}
                      </Box>
                      <Box sx={{ px: 0.75, py: 0.5 }}>
                        <Typography variant="caption" noWrap sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 9, display: 'block' }}>
                          {img.fileName}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Box>
        )}
      </Box>
    );
  };

  // =========================================================================
  // Main Render
  // =========================================================================
  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <InterfaceHeader
        title="Asset Explorer"
        color={ASSET_COLOR}
        category="link"
      />

      {/* Stats */}
      <StatsBar />

      {/* Search Bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search by SKU, collection, filename, view type..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: ASSET_COLOR, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setSearchQuery(''); setPage(1); }} sx={{ color: isDark ? '#475569' : '#cbd5e1' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: isDark ? '#1e293b' : '#fff',
              borderRadius: 2.5,
              transition: 'box-shadow 0.2s',
              '&.Mui-focused': { boxShadow: `0 0 0 3px ${ASSET_COLOR}20` },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ASSET_COLOR },
            },
          }}
        />
        <Tooltip title="Filters" arrow>
          <IconButton onClick={() => setFiltersOpen(!filtersOpen)} sx={{
            color: (categoryFilter || viewTypeFilter) ? '#fff' : (isDark ? '#94a3b8' : '#64748b'),
            bgcolor: (categoryFilter || viewTypeFilter) ? ASSET_COLOR : (isDark ? '#1e293b' : '#f1f5f9'),
            borderRadius: 2, width: 40, height: 40,
            transition: 'all 0.2s',
            '&:hover': { bgcolor: (categoryFilter || viewTypeFilter) ? ASSET_HOVER : (isDark ? '#334155' : '#e2e8f0') },
          }}>
            <Badge variant="dot" invisible={!categoryFilter && !viewTypeFilter} color="error">
              <FilterListIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Refresh" arrow>
          <IconButton onClick={() => { loadStats(); searchAssets(searchQuery, categoryFilter, viewTypeFilter, page); }} sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            bgcolor: isDark ? '#1e293b' : '#f1f5f9',
            borderRadius: 2, width: 40, height: 40,
            transition: 'all 0.2s',
            '&:hover': { bgcolor: isDark ? '#334155' : '#e2e8f0' },
          }}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Collapse in={filtersOpen}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
              sx={{ bgcolor: isDark ? '#1e293b' : '#fff' }}>
              <MenuItem value="">All Categories</MenuItem>
              {(stats?.categories || []).map(c => (
                <MenuItem key={c.name} value={c.name}>{c.name} ({c.count?.toLocaleString()})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>View Type</InputLabel>
            <Select value={viewTypeFilter} label="View Type" onChange={(e) => { setViewTypeFilter(e.target.value); setPage(1); }}
              sx={{ bgcolor: isDark ? '#1e293b' : '#fff' }}>
              <MenuItem value="">All View Types</MenuItem>
              {(stats?.viewTypes || []).map(v => (
                <MenuItem key={v.name} value={v.name}>{v.name} ({v.count?.toLocaleString()})</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(categoryFilter || viewTypeFilter) && (
            <Button size="small" onClick={() => { setCategoryFilter(''); setViewTypeFilter(''); setPage(1); }}
              sx={{ color: ASSET_COLOR, textTransform: 'none' }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Collapse>

      {/* View Type Chips */}
      <ViewTypeChart />

      {/* Tab Switcher */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, borderBottom: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 500, fontSize: 13, px: 2.5, transition: 'color 0.2s' },
            '& .Mui-selected': { color: `${ASSET_COLOR} !important`, fontWeight: 600 },
            '& .MuiTabs-indicator': { bgcolor: ASSET_COLOR, height: 2.5, borderRadius: '2px 2px 0 0' },
          }}>
          <Tab icon={<ViewModuleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Gallery" />
          <Tab icon={<TableChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Table" />
          <Tab icon={<BubbleChartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Graph" />
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 1 }}>
          {loading && <CircularProgress size={16} sx={{ color: ASSET_COLOR }} />}
          <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', fontWeight: 500, fontSize: 12 }}>
            {loading ? 'Searching...' : `${totalResults.toLocaleString()} results`}
          </Typography>
        </Box>
      </Box>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Loading skeleton */}
      {loading && activeTab === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 1.5 }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <Box key={i} sx={{ aspectRatio: '1', borderRadius: 2, bgcolor: isDark ? '#1e293b' : '#f1f5f9', animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
          ))}
        </Box>
      )}
      {loading && activeTab !== 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} sx={{ color: ASSET_COLOR }} />
        </Box>
      )}

      {/* Content */}
      {!loading && (
        <>
          {activeTab === 0 && <GalleryView />}
          {activeTab === 1 && <TableView />}
          {activeTab === 2 && <GraphView />}
        </>
      )}

      {/* Table Pagination */}
      {activeTab === 1 && totalResults > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <TablePagination
            component="div" count={totalResults} page={page - 1}
            onPageChange={(e, p) => setPage(p + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }}
            rowsPerPageOptions={[50, 100, 200]}
            sx={{ '& .MuiTablePagination-toolbar': { minHeight: 36 } }}
          />
        </Box>
      )}

      {/* Graph image panel lightbox — reuses gallery Dialog pattern */}
      <Dialog open={lightboxOpen && lightboxImages.length > 0} onClose={closeLightbox}
        maxWidth="lg" fullWidth
        PaperProps={{ sx: { bgcolor: isDark ? '#0f172a' : '#fff', maxHeight: '95vh' } }}>
        {lightboxOpen && lightboxImages.length > 0 && (() => {
          const img = lightboxImages[lightboxIndex];
          if (!img) return null;
          return (
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden', height: '85vh' }}>
              <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isDark ? '#000' : '#f1f5f9' }}>
                <Box component="img" src={img.s3Url} alt={img.fileName} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                {lightboxIndex > 0 && (
                  <IconButton onClick={() => lightboxPrev()} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}><ChevronLeftIcon /></IconButton>
                )}
                {lightboxIndex < lightboxImages.length - 1 && (
                  <IconButton onClick={() => lightboxNext()} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}><ChevronRightIcon /></IconButton>
                )}
                <Typography sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{lightboxIndex + 1} / {lightboxImages.length}</Typography>
              </Box>
              <Box sx={{ width: { xs: '100%', md: 280 }, borderLeft: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, p: 2, overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}><IconButton size="small" onClick={closeLightbox}><CloseIcon sx={{ fontSize: 18 }} /></IconButton></Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', mb: 1 }}>{img.fileName}</Typography>
                {img.viewType && <Chip label={img.viewType} size="small" sx={{ bgcolor: getViewTypeColor(img.viewType) + '20', color: getViewTypeColor(img.viewType), fontSize: 10, mb: 1.5 }} />}
                <Divider sx={{ mb: 1.5 }} />
                <Button fullWidth size="small" variant="contained" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={() => downloadAsset(img.s3Url, img.fileName)} sx={{ textTransform: 'none', fontSize: 12, bgcolor: ASSET_COLOR, mb: 1 }}>Download</Button>
                <Button fullWidth size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />} onClick={() => window.open(img.s3Url, '_blank')} sx={{ textTransform: 'none', fontSize: 12, mb: 1 }}>Open S3</Button>
                <Button fullWidth size="small" variant="outlined" startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />} onClick={() => { navigator.clipboard.writeText(img.s3Url); enqueueSnackbar('S3 URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 }); }} sx={{ textTransform: 'none', fontSize: 12 }}>Copy URL</Button>
              </Box>
            </DialogContent>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default AssetExplorer;
